import { useId, useState } from 'react';
import type { MouseEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { Trend, TrendPoint } from '@/shared/api/generated/scoring';
import { useMeasuredWidth } from '@/shared/ui/useMeasuredWidth';

/* A dataset's score over time, as the design's area chart: one line on a soft fill, three quiet
 * gridlines, a dashed marker where the score model changed, and the date and figure of whichever
 * point the pointer is nearest.
 *
 * **Only defensible points are drawn.** A null value is a score the platform declined to compute,
 * and joining the line across it would draw a measurement nobody took.
 *
 * **The model-change marker is the honest half.** A score that moved across a reweighting may not
 * reflect any change in the data, and the chart says where that boundary is rather than leaving a
 * reader to attribute the movement to their tables.
 */

const HEIGHT = 170;
const PAD = { left: 4, right: 8, top: 14, bottom: 24 } as const;

type Plotted = TrendPoint & { readonly value: number };

export function ScoreTrendChart({
  trend,
}: {
  readonly trend: Trend;
}): ReactElement | null {
  const { t, i18n } = useTranslation();
  const [ref, width] = useMeasuredWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const gradient = useId();

  const points = trend.points.filter((point): point is Plotted => point.value !== null);
  if (points.length < 2) return null;

  const values = points.map((point) => point.value);
  let low = Math.min(...values);
  let high = Math.max(...values);
  const span = high - low || 1;
  low -= span * 0.25;
  high += span * 0.2;

  const innerWidth = width - PAD.left - PAD.right;
  const innerHeight = HEIGHT - PAD.top - PAD.bottom;
  const last = points.length - 1;
  const x = (index: number): number => PAD.left + (index / last) * innerWidth;
  const y = (value: number): number =>
    PAD.top + innerHeight - ((value - low) / (high - low || 1)) * innerHeight;

  const line = points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)} ${y(point.value).toFixed(1)}`,
    )
    .join(' ');
  const area = `${line} L${x(last).toFixed(1)} ${String(PAD.top + innerHeight)} L${x(0).toFixed(1)} ${String(PAD.top + innerHeight)} Z`;

  /* Where each segment after the first begins: the first point of that segment, found in the plotted
     list by id, so a null point dropped above cannot shift the marker onto the wrong score. */
  const markers = trend.crosses_a_model_change
    ? trend.segments.slice(1).flatMap((segment) => {
        const first = segment.find((point) => point.value !== null);
        const index =
          first === undefined
            ? -1
            : points.findIndex((point) => point.score_id === first.score_id);
        return index <= 0 ? [] : [{ index, version: first?.score_model_version ?? 0 }];
      })
    : [];

  const dateOf = (iso: string): string =>
    new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const ticks = [0, Math.floor(last / 2), last];
  const shown = hover === null ? null : (points[hover] ?? null);

  const onMove = (event: MouseEvent<HTMLDivElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    const at = event.clientX - box.left;
    let best = 0;
    let distance = Number.POSITIVE_INFINITY;
    points.forEach((_, index) => {
      const gap = Math.abs(x(index) - at);
      if (gap < distance) {
        distance = gap;
        best = index;
      }
    });
    setHover(best);
  };

  return (
    <div
      ref={ref}
      className="lq-trend"
      role="img"
      aria-label={t('dataset.score.trendLabel', {
        count: points.length,
        first: points[0]?.value.toFixed(1) ?? '',
        last: points[last]?.value.toFixed(1) ?? '',
      })}
      onMouseMove={onMove}
      onMouseLeave={() => {
        setHover(null);
      }}
    >
      <svg width={width} height={HEIGHT} className="lq-trend__svg" aria-hidden="true">
        <defs>
          <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="lq-trend__stopTop" />
            <stop offset="100%" className="lq-trend__stopBottom" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((fraction) => (
          <line
            key={fraction}
            className="lq-trend__grid"
            x1={PAD.left}
            x2={width - PAD.right}
            y1={PAD.top + innerHeight * fraction}
            y2={PAD.top + innerHeight * fraction}
          />
        ))}
        {markers.map((marker) => (
          <g key={marker.index}>
            <line
              className="lq-trend__marker"
              x1={x(marker.index)}
              x2={x(marker.index)}
              y1={PAD.top}
              y2={PAD.top + innerHeight}
            />
            <text
              className="lq-trend__markerLabel"
              x={x(marker.index) + 6}
              y={PAD.top + 10}
            >
              {t('dataset.score.modelMarker', { version: marker.version })}
            </text>
          </g>
        ))}
        <path d={area} fill={`url(#${gradient})`} />
        <path d={line} className="lq-trend__line" />
        <circle
          className="lq-trend__last"
          cx={x(last)}
          cy={y(points[last]?.value ?? 0)}
          r={3.5}
        />
        {shown === null || hover === null ? null : (
          <g>
            <line
              className="lq-trend__cross"
              x1={x(hover)}
              x2={x(hover)}
              y1={PAD.top}
              y2={PAD.top + innerHeight}
            />
            <circle className="lq-trend__dot" cx={x(hover)} cy={y(shown.value)} r={4.5} />
          </g>
        )}
        {ticks.map((index) => {
          const point = points[index];
          if (point === undefined) return null;
          return (
            <text
              key={index}
              className="lq-trend__tick"
              x={x(index)}
              y={HEIGHT - 6}
              textAnchor={index === 0 ? 'start' : index === last ? 'end' : 'middle'}
            >
              {dateOf(point.computed_at)}
            </text>
          );
        })}
      </svg>
      {shown === null || hover === null ? null : (
        <div
          className="lq-trend__tip"
          style={{
            left: Math.min(Math.max(x(hover) - 70, 0), width - 140),
            top: Math.max(y(shown.value) - 62, -8),
          }}
        >
          <div className="lq-trend__tipWhen">{dateOf(shown.computed_at)}</div>
          <div className="lq-trend__tipValue">{shown.value.toFixed(1)}</div>
          <div className="lq-trend__tipWhen">
            {t('dataset.score.pointSub', {
              coverage:
                shown.column_coverage === null
                  ? '—'
                  : `${String(Math.round(shown.column_coverage * 100))}%`,
              version: shown.score_model_version,
            })}
          </div>
        </div>
      )}
    </div>
  );
}
